import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { AuthenticatedLayout } from '@/components/layout/authenticated-layout'
import { FullWidthLayout } from '@/components/layout/full-width-layout'
import {
  AssociatedPartiesPage,
  AssociatedPartyDetailsPage,
} from '@/features/associated-parties'
import {
  PackagingItemDetailsPage,
  PackedItemDetailsPage,
  PackedItemsPage,
  PackagingItemsPage,
  PackagingItemIssuesPage,
  PackagingSupplierDetailsPage,
  PackagingSuppliersPage,
} from '@/features/packaging-suppliers'
import { machineryDirectiveComplianceRoute } from '@/features/compliance-studio/machinery-directive/routes'
import { LlmChatPage } from '@/features/llm-chat/LlmChatPage'
import {
  ProductCatalogListPage,
  ProductDetailPage,
} from '@/features/product-catalog'
import { NewsroomPage } from '@/features/regulatory-news'
import RadarReviewPage from '@/features/radar-review/pages/RadarReviewPage'
import RadarScopeTierBatchPage from '@/features/radar-scope-tier/pages/RadarScopeTierBatchPage'
import RadarScopeTierPage from '@/features/radar-scope-tier/pages/RadarScopeTierPage'
import RadarLegislationCorpusPage from '@/features/radar-legislation-corpus/pages/RadarLegislationCorpusPage'
import IngestionDiagnosticsListPage from '@/features/ingestion-diagnostics/pages/IngestionDiagnosticsListPage'
import IngestionDetailPage from '@/features/ingestion-diagnostics/pages/IngestionDetailPage'
import IngestionErrorsPage from '@/features/ingestion-diagnostics/pages/IngestionErrorsPage'
import { Settings } from '@/features/settings'
import { SettingsAccount } from '@/features/settings/account'
import { SettingsLanguage } from '@/features/settings/language'
import { SettingsLlm } from '@/features/settings/llm'
import { SettingsOrganization } from '@/features/settings/organization'
import { SettingsTheme } from '@/features/settings/theme'
import { StandardsLibrary } from '@/features/standards-library'
import { StandardDetail } from '@/features/standards-library/pages/StandardDetail'
import { ArtemEarthOnlyRoute } from './ArtemEarthOnlyRoute'
import { SettingsFullAccessRoute } from './SettingsFullAccessRoute'
import {
  LegacyPackagingItemDetailRedirect,
  LegacyPackagingSupplierDetailRedirect,
  LegacyPackagingSuppliersListRedirect,
} from './LegacyPackagingSuppliersRedirect'
import { PackagingSuppliersRoute } from './PackagingSuppliersRoute'

function LegacyProjectPathRedirect() {
  const location = useLocation()
  return (
    <Navigate
      to={`/product-catalog${location.search}${location.hash}`}
      replace
    />
  )
}

export default function App() {
  return (
    <Routes>
      <Route path='/' element={<AuthenticatedLayout />}>
        {/* Keep product catalog as the default landing page for authenticated users. */}
        <Route index element={<Navigate to='/product-catalog' replace />} />
        <Route path='llm-chat' element={<LlmChatPage />} />
        <Route path='llm-chat/:sessionId' element={<LlmChatPage />} />
        <Route
          path='newsroom'
          element={
            <ArtemEarthOnlyRoute>
              <NewsroomPage />
            </ArtemEarthOnlyRoute>
          }
        />
        <Route
          path='newsroom/inbox'
          element={
            <ArtemEarthOnlyRoute>
              <Navigate to='/newsroom' replace />
            </ArtemEarthOnlyRoute>
          }
        />
        <Route
          path='newsroom/settings'
          element={
            <ArtemEarthOnlyRoute>
              <Navigate to='/newsroom' replace />
            </ArtemEarthOnlyRoute>
          }
        />
        <Route
          path='regulatory-news'
          element={
            <ArtemEarthOnlyRoute>
              <Navigate to='/newsroom' replace />
            </ArtemEarthOnlyRoute>
          }
        />
        <Route
          path='regulatory-news/inbox'
          element={
            <ArtemEarthOnlyRoute>
              <Navigate to='/newsroom' replace />
            </ArtemEarthOnlyRoute>
          }
        />
        <Route
          path='regulatory-news/settings'
          element={
            <ArtemEarthOnlyRoute>
              <Navigate to='/newsroom' replace />
            </ArtemEarthOnlyRoute>
          }
        />
        <Route path='product-catalog' element={<ProductCatalogListPage />} />
        <Route
          path='product-catalog/:productId/*'
          element={<ProductDetailPage />}
        />
        <Route path='standards-library' element={<StandardsLibrary />} />
        <Route path='associated-parties' element={<AssociatedPartiesPage />} />
        <Route
          path='associated-parties/:associatedPartyId'
          element={<AssociatedPartyDetailsPage />}
        />
        <Route
          path='packaging/suppliers'
          element={
            <PackagingSuppliersRoute>
              <PackagingSuppliersPage />
            </PackagingSuppliersRoute>
          }
        />
        <Route
          path='packaging/suppliers/:packagingSupplierId'
          element={
            <PackagingSuppliersRoute>
              <PackagingSupplierDetailsPage />
            </PackagingSuppliersRoute>
          }
        />
        <Route
          path='packaging/suppliers/:packagingSupplierId/items/:itemId'
          element={
            <PackagingSuppliersRoute>
              <PackagingItemDetailsPage />
            </PackagingSuppliersRoute>
          }
        />
        <Route
          path='packaging/packaging-items'
          element={
            <PackagingSuppliersRoute>
              <PackagingItemsPage />
            </PackagingSuppliersRoute>
          }
        />
        <Route
          path='packaging/issues'
          element={
            <PackagingSuppliersRoute>
              <PackagingItemIssuesPage />
            </PackagingSuppliersRoute>
          }
        />
        <Route
          path='packaging/packed-items'
          element={
            <PackagingSuppliersRoute>
              <PackedItemsPage />
            </PackagingSuppliersRoute>
          }
        />
        <Route
          path='packaging/packed-items/:packedItemId'
          element={
            <PackagingSuppliersRoute>
              <PackedItemDetailsPage />
            </PackagingSuppliersRoute>
          }
        />
        <Route
          path='packaging-suppliers'
          element={
            <PackagingSuppliersRoute>
              <LegacyPackagingSuppliersListRedirect />
            </PackagingSuppliersRoute>
          }
        />
        <Route
          path='packaging-suppliers/:packagingSupplierId'
          element={
            <PackagingSuppliersRoute>
              <LegacyPackagingSupplierDetailRedirect />
            </PackagingSuppliersRoute>
          }
        />
        <Route
          path='packaging-suppliers/:packagingSupplierId/items/:itemId'
          element={
            <PackagingSuppliersRoute>
              <LegacyPackagingItemDetailRedirect />
            </PackagingSuppliersRoute>
          }
        />
        <Route
          path='standards-library/standards/:standardId'
          element={<StandardDetail />}
        />
        {machineryDirectiveComplianceRoute}
        <Route path='project/*' element={<LegacyProjectPathRedirect />} />
        <Route path='radar/review' element={<RadarReviewPage />} />
        <Route path='settings' element={<Settings />}>
          <Route path='account' element={<SettingsAccount />} />
          <Route
            path='organization'
            element={
              <SettingsFullAccessRoute>
                <SettingsOrganization />
              </SettingsFullAccessRoute>
            }
          />
          <Route
            path='llm'
            element={
              <SettingsFullAccessRoute>
                <SettingsLlm />
              </SettingsFullAccessRoute>
            }
          />
          <Route path='theme' element={<SettingsTheme />} />
          <Route path='language' element={<SettingsLanguage />} />
        </Route>
      </Route>
      <Route
        path='radar/scope-tier'
        element={
          <ArtemEarthOnlyRoute>
            <FullWidthLayout>
              <RadarScopeTierPage />
            </FullWidthLayout>
          </ArtemEarthOnlyRoute>
        }
      />
      <Route
        path='radar/scope-tier/batch'
        element={
          <ArtemEarthOnlyRoute>
            <FullWidthLayout>
              <RadarScopeTierBatchPage />
            </FullWidthLayout>
          </ArtemEarthOnlyRoute>
        }
      />
      <Route
        path='radar/legislation-corpus'
        element={
          <ArtemEarthOnlyRoute>
            <FullWidthLayout>
              <RadarLegislationCorpusPage />
            </FullWidthLayout>
          </ArtemEarthOnlyRoute>
        }
      />
      <Route
        path='admin/diagnostics/ingestions'
        element={
          <ArtemEarthOnlyRoute>
            <FullWidthLayout>
              <IngestionDiagnosticsListPage />
            </FullWidthLayout>
          </ArtemEarthOnlyRoute>
        }
      />
      <Route
        path='admin/diagnostics/ingestions/:sourceId'
        element={
          <ArtemEarthOnlyRoute>
            <FullWidthLayout>
              <IngestionDetailPage />
            </FullWidthLayout>
          </ArtemEarthOnlyRoute>
        }
      />
      <Route
        path='admin/diagnostics/ingestion-errors'
        element={
          <ArtemEarthOnlyRoute>
            <FullWidthLayout>
              <IngestionErrorsPage />
            </FullWidthLayout>
          </ArtemEarthOnlyRoute>
        }
      />
      <Route path='*' element={<Navigate to='/' replace />} />
    </Routes>
  )
}
